import React from "react";
import {
    Box,
    Container,
    Paper,
    Skeleton,
    Stack,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

export function ResultsPageContainer({ children, maxWidth = "xl", ...props }) {
    return (
        <Container
            maxWidth={maxWidth}
            sx={{
                py: { xs: 2, md: 3 },
                pb: { xs: 4, md: 6 },
                px: { xs: 1.5, sm: 2 },
            }}
            {...props}
        >
            {children}
        </Container>
    );
}

export function ResultsPageHeader({ eyebrow = "Exams & Results", title, subtitle, action }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                mb: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background: (theme) =>
                    `linear-gradient(125deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
                        theme.palette.secondary.main,
                        0.05
                    )} 100%)`,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                justifyContent="space-between"
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.12em" }}>
                        {eyebrow}
                    </Typography>
                    <Typography
                        variant="h4"
                        component="h1"
                        fontWeight={800}
                        sx={{ fontSize: { xs: "1.35rem", sm: "1.65rem" }, mt: 0.25 }}
                    >
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
            </Stack>
        </Paper>
    );
}

export function KpiCard({ title, value, icon: Icon, color = "primary", loading }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
            {loading ? (
                <>
                    <Skeleton width="55%" height={18} sx={{ mb: 1 }} />
                    <Skeleton width="75%" height={34} />
                </>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {title}
                        </Typography>
                        {Icon ? <Icon sx={{ color: `${color}.main`, fontSize: 22 }} /> : null}
                    </Stack>
                    <Typography variant="h6" fontWeight={800} color={`${color}.main`} sx={{ wordBreak: "break-word" }}>
                        {value}
                    </Typography>
                </>
            )}
        </Paper>
    );
}

export function ResultsSectionPaper({ title, children, action }) {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {title}
                </Typography>
                {action}
            </Stack>
            {children}
        </Paper>
    );
}

export function ResponsiveTableWrap({ children }) {
    return (
        <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {children}
        </Box>
    );
}

export function EmptyResultsRow({ colSpan, message = "No data yet." }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    {message}
                </Typography>
            </TableCell>
        </TableRow>
    );
}
